import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, ArrowRight, Quote } from 'lucide-react';
import { getReviews, getProducts } from '../lib/api';
import { Button } from '../components/ui/button';

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reviewsData, productsData] = await Promise.all([
          getReviews(),
          getProducts()
        ]);
        setReviews(reviewsData);
        setProducts(productsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product?.name || 'Produit GHRB';
  };

  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : '5.0';

  const ratingCounts = [5, 4, 3, 2, 1].map(rating => ({
    rating,
    count: reviews.filter(r => r.rating === rating).length,
    percentage: reviews.length > 0 ? (reviews.filter(r => r.rating === rating).length / reviews.length) * 100 : 0
  }));

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const stagger = {
    visible: { transition: { staggerChildren: 0.1 } }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-ghrb-orange border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="reviews-page" className="min-h-screen bg-white">
      {/* Header */}
      <section className="py-16 lg:py-24 bg-ghrb-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="max-w-2xl"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <p className="font-outfit text-xs uppercase tracking-[0.2em] text-ghrb-orange mb-4">
              Témoignages
            </p>
            <h1 className="font-outfit text-4xl lg:text-5xl font-bold tracking-tight text-ghrb-black mb-6">
              Avis clients
            </h1>
            <p className="font-jakarta text-lg text-ghrb-gray-dark">
              Découvrez ce que nos clients pensent de nos produits. 
              Des avis authentiques et vérifiés.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Rating Summary */}
      <section className="py-12 lg:py-16 border-b border-ghrb-black/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Overall Rating */}
            <motion.div 
              className="text-center lg:text-left"
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
            >
              <div className="flex items-center justify-center lg:justify-start gap-4 mb-4">
                <span className="font-outfit text-6xl lg:text-7xl font-bold text-ghrb-black">
                  {averageRating}
                </span>
                <div>
                  <div className="flex gap-1 mb-1">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`w-6 h-6 ${i < Math.round(parseFloat(averageRating)) ? 'text-ghrb-orange fill-ghrb-orange' : 'text-gray-200'}`}
                      />
                    ))}
                  </div>
                  <p className="font-jakarta text-sm text-ghrb-gray-dark">
                    Basé sur {reviews.length} avis
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Rating Breakdown */}
            <motion.div 
              className="space-y-3"
              initial="hidden"
              animate="visible"
              variants={stagger}
            >
              {ratingCounts.map(({ rating, count, percentage }) => (
                <motion.div key={rating} variants={fadeInUp} className="flex items-center gap-3">
                  <span className="font-jakarta text-sm text-ghrb-gray-dark w-8">{rating} ★</span>
                  <div className="flex-1 h-2 bg-ghrb-gray rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-ghrb-orange rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="font-jakarta text-sm text-ghrb-gray-dark w-12 text-right">
                    {count}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
            initial="hidden"
            animate="visible"
            variants={stagger}
          >
            {reviews.map((review, index) => (
              <motion.div 
                key={review.id}
                variants={fadeInUp}
                className="bg-ghrb-gray p-6 lg:p-8 rounded-2xl relative"
                data-testid={`review-card-${index}`}
              >
                <Quote className="absolute top-6 right-6 w-8 h-8 text-ghrb-black/5" />
                
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={`w-5 h-5 ${i < review.rating ? 'text-ghrb-orange fill-ghrb-orange' : 'text-gray-300'}`}
                    />
                  ))}
                </div>
                
                <p className="font-jakarta text-ghrb-black mb-6 leading-relaxed">
                  "{review.comment}"
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-ghrb-orange/10 rounded-full flex items-center justify-center">
                      <span className="font-outfit font-semibold text-ghrb-orange">
                        {review.author_name?.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-jakarta font-medium text-ghrb-black">{review.author_name}</p>
                      <p className="font-jakarta text-xs text-ghrb-gray-dark">
                        {getProductName(review.product_id)}
                      </p>
                    </div>
                  </div>
                  {review.is_verified && (
                    <span className="text-xs text-ghrb-orange font-medium bg-ghrb-orange/10 px-2 py-1 rounded-full">
                      ✓ Vérifié
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {reviews.length === 0 && (
            <div className="text-center py-16">
              <p className="font-jakarta text-ghrb-gray-dark">
                Aucun avis pour le moment.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-ghrb-black text-white relative grain-overlay">
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 variants={fadeInUp} className="font-outfit text-3xl lg:text-4xl font-bold tracking-tight mb-6">
              Rejoignez nos clients satisfaits
            </motion.h2>
            <motion.p variants={fadeInUp} className="font-jakarta text-lg text-white/70 mb-10">
              Découvrez par vous-même pourquoi des milliers de clients nous font confiance.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link to="/produits">
                <Button 
                  size="lg"
                  className="bg-ghrb-orange hover:bg-ghrb-orange-hover text-white font-jakarta font-semibold px-10 py-6 rounded-full transition-transform hover:scale-105"
                  data-testid="reviews-cta"
                >
                  Découvrir nos produits
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ReviewsPage;
